import React, { useEffect, useRef, useState } from 'react';
import { 
  FaRupeeSign, FaCalendarAlt, FaFileInvoice, FaClock,
  FaCheckCircle, FaArrowRight, FaInfoCircle, FaRegClock,
  FaPercent, FaUniversity, FaUsers, FaBookOpen,FaPhone,FaEnvelope
} from 'react-icons/fa';
import { MdOutlinePolicy, MdOutlinePayment, MdOutlineReceipt } from 'react-icons/md';

const FeeRefundPolicyPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const refundRules = [
    {
      title: "Cancellation Before Commencement of Classes",
      percentage: "100%",
      description: "Full refund of fees (excluding registration fee) if admission is cancelled before the start of academic session.",
      deadline: "15 days before session start",
      icon: <FaCheckCircle />
    },
    {
      title: "Cancellation Within 15 Days of Session Start",
      percentage: "75%",
      description: "75% refund of tuition fees if cancelled within 15 days from the commencement of classes.",
      deadline: "Within 15 days",
      icon: <FaCalendarAlt />
    },
    {
      title: "Cancellation Within 30 Days of Session Start",
      percentage: "50%",
      description: "50% refund of tuition fees if cancelled within 30 days from the commencement of classes.",
      deadline: "Within 30 days",
      icon: <FaRegClock />
    },
    {
      title: "Cancellation After 30 Days",
      percentage: "0%",
      description: "No refund will be provided for cancellations made after 30 days from the commencement of classes.",
      deadline: "After 30 days",
      icon: <FaInfoCircle />
    }
  ];

  const nonRefundableFees = [
    "Registration Fee: ₹500 (Non-refundable)",
    "Application Processing Fee: ₹200 (Non-refundable)",
    "Library Security Deposit (Refundable after course completion)",
    "Laboratory Security Deposit (Refundable after course completion)"
  ];

  const refundProcess = [
    { step: 1, title: "Submit Application", description: "Submit written application for cancellation to the college office." },
    { step: 2, title: "Document Submission", description: "Submit original fee receipt and admission documents." },
    { step: 3, title: "Verification", description: "College verifies the application and calculates refund amount." },
    { step: 4, title: "Processing", description: "Refund is processed within 30 working days." },
    { step: 5, title: "Refund Mode", description: "Amount is transferred to the bank account provided." }
  ];

  return (
    <section ref={sectionRef} className="w-full bg-gradient-to-br from-blue-50 via-white to-blue-50 py-16">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          
          {/* Decorative Top */}
          <div className="flex justify-center gap-3 mb-6">
            <div className="w-12 h-1 bg-blue-500 rounded-full"></div>
            <div className="w-8 h-1 bg-blue-600 rounded-full"></div>
            <div className="w-6 h-1 bg-blue-700 rounded-full"></div>
          </div>

          {/* Breadcrumb */}
          <div className={`flex items-center justify-center gap-2 text-xs mb-6 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'
          }`}>
            <span className="text-gray-500">Home</span>
            <span className="text-gray-400">/</span>
            <span className="text-blue-600 font-semibold">Fee Refund Policy</span>
          </div>

          {/* Heading */}
          <div className="text-center mb-10">
            <div className="flex justify-center mb-3">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-3 rounded-2xl shadow-lg">
                <MdOutlinePolicy className="text-white text-3xl" />
              </div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-3">
              <span className="bg-gradient-to-r from-blue-700 to-indigo-800 bg-clip-text text-transparent">
                Fee Refund Policy
              </span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mx-auto"></div>
            <p className="text-gray-600 text-sm mt-4 max-w-2xl mx-auto">
              Understand our fee refund guidelines and cancellation policy for academic session 2024-25
            </p>
          </div>

          {/* Refund Rules Grid */}
          <div className={`grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            {refundRules.map((rule, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1">
                <div className={`p-4 text-white text-center ${
                  idx === 0 ? 'bg-green-600' : idx === 1 ? 'bg-blue-600' : idx === 2 ? 'bg-yellow-600' : 'bg-red-600'
                }`}>
                  <div className="text-3xl mb-1 flex justify-center">{rule.icon}</div>
                  <div className="text-2xl font-bold">{rule.percentage}</div>
                  <div className="text-xs opacity-90">Refund</div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-gray-800 text-sm mb-2">{rule.title}</h3>
                  <p className="text-gray-600 text-xs leading-relaxed mb-2">{rule.description}</p>
                  <p className="text-blue-600 text-xs font-semibold">⏱️ {rule.deadline}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Non-Refundable Fees Section */}
          <div className={`grid lg:grid-cols-2 gap-8 mb-12 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '100ms' }}>
            
            {/* Non-Refundable Fees */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <FaRupeeSign className="text-red-600 text-xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Non-Refundable Fees</h3>
              </div>
              <div className="space-y-3">
                {nonRefundableFees.map((fee, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full mt-1.5"></div>
                    <p className="text-gray-600 text-sm">{fee}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-red-50 rounded-lg">
                <p className="text-red-600 text-xs">⚠️ Note: These fees are non-refundable under any circumstances.</p>
              </div>
            </div>

            {/* Important Notes */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <FaInfoCircle className="text-yellow-600 text-xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Important Notes</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <FaCheckCircle className="text-green-500 text-sm mt-0.5" />
                  <p className="text-gray-600 text-sm">Refund will be processed within 30 working days after application submission.</p>
                </div>
                <div className="flex items-start gap-2">
                  <FaCheckCircle className="text-green-500 text-sm mt-0.5" />
                  <p className="text-gray-600 text-sm">Security deposits are refundable after course completion (subject to no dues).</p>
                </div>
                <div className="flex items-start gap-2">
                  <FaCheckCircle className="text-green-500 text-sm mt-0.5" />
                  <p className="text-gray-600 text-sm">Refund amount will be credited to the same bank account from which fees were paid.</p>
                </div>
                <div className="flex items-start gap-2">
                  <FaCheckCircle className="text-green-500 text-sm mt-0.5" />
                  <p className="text-gray-600 text-sm">Original fee receipt must be submitted for refund processing.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Refund Process */}
          <div className={`bg-white rounded-2xl shadow-lg p-6 mb-10 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '200ms' }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <MdOutlinePayment className="text-blue-600 text-xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Refund Process</h3>
            </div>
            <div className="grid md:grid-cols-5 gap-4">
              {refundProcess.map((step, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold mx-auto mb-2">
                    {step.step}
                  </div>
                  <p className="font-semibold text-gray-800 text-sm">{step.title}</p>
                  <p className="text-gray-500 text-xs mt-1">{step.description}</p>
                  {idx < refundProcess.length - 1 && (
                    <div className="hidden lg:block absolute right-0 top-5 w-full h-0.5 bg-gray-200 -z-10"></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Contact for Refund */}
          <div className={`bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-center text-white transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '300ms' }}>
            <h3 className="text-xl font-bold mb-2">Need Help with Refund?</h3>
            <p className="text-blue-100 text-sm mb-4">Contact our accounts department for any refund-related queries</p>
            <div className="flex flex-col md:flex-row justify-center gap-4">
              <div className="flex items-center justify-center gap-2">
                <FaPhone className="text-yellow-400" />
                <span className="text-sm">+91 7080 100 333</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <FaEnvelope className="text-yellow-400" />
                <span className="text-sm">accounts@bnsmahila.edu</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <FaRegClock className="text-yellow-400" />
                <span className="text-sm">Mon-Fri: 10:00 AM - 4:00 PM</span>
              </div>
            </div>
          </div>

          {/* Bottom Decorative */}
          <div className="mt-10 flex justify-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default FeeRefundPolicyPage;